import { useId } from 'react';
import { Slider } from '@voll-intelligence/ui-kit/ui/slider';
import {
  DAY_MINUTES_END,
  formatDayMinute,
  priceBounds,
  priceHistogram,
  type DayMinuteRange,
  type FlightFilterState,
} from '../filter_logic.js';
import type { FlightOfferSource } from '../flight_offer_source.js';
import type { FlightFilterCopy } from './filter_copy.js';
import { PanelSection } from './filter_panel_chrome.js';
import { PriceField } from './price_field.js';

/* As duas seções de RÉGUA do `FlightFilterPanel` (horários e preço), fora do
   `filter_panel.tsx` pelo teto de linhas do arquivo. */

type SectionProps = {
  state: FlightFilterState;
  onPatch: (p: Partial<FlightFilterState>) => void;
  copy: FlightFilterCopy;
};

/**
 * UM eixo `Horários` com as duas réguas — `Saída` e `Chegada` — em vez de dois
 * grupos irmãos: o desenho da bancada trata partida e chegada como a MESMA
 * pergunta feita duas vezes, e em corporativo a hora que decide é a de chegar
 * (a pessoa voa PARA um compromisso). Os blocos pré-determinados
 * (Madrugada/Manhã/Tarde/Noite) viraram régua contínua de dois polegares
 * (#2112 — decisão da planilha; o template do protótipo ainda mostra blocos, e
 * em conflito vale a régua). O `aria-label` de cada metade segue o rótulo
 * longo — "Saída" sozinho não diz a um leitor de tela de que eixo é a metade.
 */
export function TimeSection({ state, onPatch, copy }: Readonly<SectionProps>) {
  const subIdBase = useId();
  const halves: ReadonlyArray<{
    axis: 'departureRange' | 'arrivalRange';
    sub: string;
    long: string;
  }> = [
    { axis: 'departureRange', sub: copy.time.departs, long: copy.time.departureTime },
    { axis: 'arrivalRange', sub: copy.time.arrives, long: copy.time.arrivalTime },
  ];
  return (
    <PanelSection label={copy.time.label}>
      {halves.map(({ axis, sub, long }) => (
        <div key={axis} role="group" aria-label={long} className="ffp-half">
          <span className="ffp-half-row">
            <span id={`${subIdBase}-${axis}`} className="ffp-sublabel">
              {sub}
            </span>
            {/* O intervalo LEGÍVEL ("06:00 – 11:59") — os polegares sozinhos não
                dizem onde estão. */}
            <span className="ffp-timeval">
              {formatDayMinute(state[axis][0])} – {formatDayMinute(state[axis][1])}
            </span>
          </span>
          <DayRangeSlider
            labelledBy={`${subIdBase}-${axis}`}
            value={state[axis]}
            onChange={(range) => {
              onPatch({ [axis]: range });
            }}
          />
        </div>
      ))}
    </PanelSection>
  );
}

/** A janela MÍNIMA da régua de horário, em minutos. Sem ela o `End` no polegar
 *  de início (ou um arrasto por cima do outro) colapsava a faixa em
 *  "23:59 – 23:59" com zero voos (#3239 C5); meia hora é a menor janela que
 *  ainda separa voos de verdade em vez de zerar a lista. */
const DAY_RANGE_MIN_GAP_MINUTES = 30;

/**
 * Régua de dois polegares sobre os minutos do dia (00:00–23:59), no Slider do
 * DS — o mesmo padrão visual da régua de preço. Passo de 1 minuto: um passo
 * maior nunca alcançaria o teto 23:59 (1439 não é múltiplo de 5), e a faixa
 * "cheia" ficaria inalcançável depois do primeiro arrasto.
 *
 * Os polegares NÃO se cruzam (`thumbCollisionBehavior="none"`): o padrão do Base
 * UI é `push`, e o polegar arrastado levava o outro junto até o fim do trilho.
 * `minStepsBetweenValues` vale para o teclado (Home/End param no vizinho menos a
 * folga) e para o ponteiro.
 *
 * O embrulho `genui2` LOCAL dá tokens ao Slider no drawer legado do chat, que
 * não tem a raiz do DS por cima — ver `PolicySection` no `filter_panel.tsx`.
 */
function DayRangeSlider({
  labelledBy,
  value,
  onChange,
}: Readonly<{
  /** Id do sub-rótulo da metade ("Saída"/"Chegada"). O root do Slider é um
   *  `role=group` próprio — um `aria-label` igual ao da metade criaria dois
   *  grupos homônimos aninhados; o labelledby dá nome ao slider (e aos
   *  polegares, que o herdam) sem duplicar o nome do grupo de fora. */
  labelledBy: string;
  value: DayMinuteRange;
  onChange: (range: DayMinuteRange) => void;
}>) {
  return (
    <span className="genui2 ffp-slider">
      <Slider
        min={0}
        max={DAY_MINUTES_END}
        step={1}
        minStepsBetweenValues={DAY_RANGE_MIN_GAP_MINUTES}
        thumbCollisionBehavior="none"
        value={[value[0], value[1]]}
        aria-labelledby={labelledBy}
        onValueChange={(range: readonly number[]) => {
          const [start, end] = range;
          if (start === undefined || end === undefined) return;
          onChange([start, end]);
        }}
      />
    </span>
  );
}

/**
 * Preço com DUAS pontas (#2110): histograma + régua de dois polegares do DS +
 * campos de valor mín/máx sincronizados nos dois sentidos. O piso e o teto do
 * trilho são do CONJUNTO quando as facetas chegam; o corte vivo é o do estado.
 */
export function PriceSection({
  offers,
  state,
  currency,
  onPatch,
  bounds,
  copy,
  locale,
}: {
  offers: readonly FlightOfferSource[];
  /** O locale dos campos de valor (separador de milhar). */
  locale: string;
  /** Faixa do CONJUNTO (servidor). Ausente, cai na faixa da lista em mão — que
   *  encolhe a cada corte e faria o trilho andar debaixo do dedo. */
  bounds?: { min: number; max: number };
  currency: string | undefined;
} & SectionProps) {
  const labelId = useId();
  const [min, max] = bounds ? [Math.floor(bounds.min), Math.ceil(bounds.max)] : priceBounds(offers);
  if (!currency || max <= min) return null;
  // O estado guarda o corte cru (`minPrice: 0` = sem piso); o trilho e os campos
  // mostram o valor DENTRO da faixa corrente.
  const lo = Math.min(Math.max(state.minPrice, min), max);
  const hi = Math.min(Math.max(state.maxPrice, min), max);
  // Ponta DIGITADA que ALCANÇA ou cruza a outra (#3239 C5): a ponta digitada
  // vale e a alcançada é SOLTA pro limite do conjunto — mínimo 3000 sobre máximo
  // 2000 vira "3000 – teto", não "2000 – 2000" (o clamp antigo colapsava a faixa
  // em silêncio e a lista zerava). A igualdade entra aqui de propósito: o campo
  // aceita a outra ponta como valor vivo (`ceiling`/`floor`), e sem soltar no
  // empate a faixa colapsaria no mesmo valor pelos dois lados. `0` é o "sem
  // piso" canônico do estado.
  const commitMin = (v: number) => {
    onPatch(v >= hi ? { minPrice: v, maxPrice: max } : { minPrice: v });
  };
  const commitMax = (v: number) => {
    onPatch(v <= lo ? { minPrice: 0, maxPrice: v } : { maxPrice: v });
  };
  return (
    <PanelSection label={copy.price.label} labelId={labelId}>
      {/* O histograma é o que transforma a régua em instrumento: sem o desenho da
          distribuição, arrastar as pontas é chutar onde estão os voos. */}
      <div className="ffp-hist" aria-hidden="true">
        {priceHistogram(offers, state.minPrice, state.maxPrice, 28).map((bar, i) => (
          <span
            key={`${String(bar.on)}-${String(bar.height)}-${String(i)}`}
            className={bar.on ? 'on' : ''}
            style={{ height: `${String(Math.round(bar.height * 100))}%` }}
          />
        ))}
      </div>
      <span className="genui2 ffp-slider">
        <Slider
          min={min}
          max={max}
          step={1}
          // Sem cruzar nem empurrar (o `push` padrão mudava as DUAS pontas num
          // gesto e só uma chegava ao estado) — ver `DayRangeSlider`.
          minStepsBetweenValues={1}
          thumbCollisionBehavior="none"
          value={[lo, hi]}
          // `aria-labelledby`, não `aria-label`: os polegares (inputs role=slider)
          // herdam SÓ o labelledby da raiz no Base UI — com `aria-label` eles
          // ficavam sem nome (#3239 C6). Mesma régua do `DayRangeSlider`.
          aria-labelledby={labelId}
          onValueChange={(range: readonly number[]) => {
            const [nextLo, nextHi] = range;
            if (nextLo === undefined || nextHi === undefined) return;
            // Um polegar por gesto: patch de UM campo por vez (contrato da
            // telemetria — a chave única identifica o eixo).
            if (nextLo !== lo) onPatch({ minPrice: nextLo });
            else if (nextHi !== hi) onPatch({ maxPrice: nextHi });
          }}
        />
      </span>
      <div className="ffp-pricefields">
        <PriceField
          label={copy.price.min}
          locale={locale}
          value={lo}
          floor={min}
          ceiling={hi}
          limit={{ min, max }}
          currency={currency}
          onCommit={commitMin}
        />
        <PriceField
          label={copy.price.max}
          locale={locale}
          value={hi}
          floor={lo}
          ceiling={max}
          limit={{ min, max }}
          currency={currency}
          onCommit={commitMax}
        />
      </div>
    </PanelSection>
  );
}
